import { Fragment, FC } from "react";
import {
  Box,
  InputAdornment,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

/**
 * Shared style for the small snow-level value box. Hides the native number
 * spinners (redundant next to the slider) and widens the field so two-digit
 * values plus the ″ adornment stay fully visible on smaller screens.
 */
export const snowValueFieldSx: SxProps<Theme> = {
  width: "104px",
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

export interface ElevationLevelValue {
  /** Present when editing existing report rows; absent when creating. */
  id?: string;
  min: number;
  max: number;
  snowAmount: number;
}

interface ElevationLevelSlidersProps {
  levels: ElevationLevelValue[];
  onChange: (index: number, snowAmount: number) => void;
}

const MIN_SNOW = 0;
const MAX_SNOW = 40;

const clampSnow = (value: number) =>
  Math.max(MIN_SNOW, Math.min(MAX_SNOW, value));

/**
 * Renders one snow-level control per configured elevation level. The value can
 * be typed directly or set with the slider; both stay in sync. Emits grid
 * children (range label + control) so it flows into the parent form grid,
 * matching the "Weather Severity" / "Current Snowfall" rows.
 */
const ElevationLevelSliders: FC<ElevationLevelSlidersProps> = ({
  levels,
  onChange,
}) => (
  <>
    {levels.map((level, index) => (
      <Fragment key={level.id ?? `${level.min}-${level.max}`}>
        <Typography fontWeight={600}>
          {`${level.min} - ${level.max} ft`}
        </Typography>
        <Box display="flex" flexDirection="row" alignItems="center" gap="24px">
          <TextField
            type="number"
            inputProps={{
              style: { textAlign: "center" },
              min: MIN_SNOW,
              max: MAX_SNOW,
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">″</InputAdornment>,
            }}
            sx={snowValueFieldSx}
            value={level.snowAmount}
            onChange={(e) => {
              const raw = e.target.value;
              onChange(index, raw === "" ? MIN_SNOW : clampSnow(Number(raw)));
            }}
          />
          <Slider
            value={level.snowAmount}
            max={MAX_SNOW}
            min={MIN_SNOW}
            step={1}
            onChange={(_e, value) => onChange(index, value as number)}
          />
        </Box>
      </Fragment>
    ))}
  </>
);

export default ElevationLevelSliders;
